import { Chip, InputLabel, makeStyles } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import ChipInput from 'material-ui-chip-input';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toCssValue } from '../../../core/utils/theme.utils';
import { WildcardTagsFilter } from '../../../generated/model/models';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import ReadableStatementView from '../../Inputs/ReadableStatementView';
import SwitchCompaniesUnknowns from '../../Inputs/SwitchCompaniesUnknowns';

export enum TagsFilterLabelFormatters {
  Operator = 'Operator',
}

export const tagsLabelFormattersMap = {
  [TagsFilterLabelFormatters.Operator]: (text: string) => text.split(' ').join(' (AND) '),
};

interface Props {
  root?: boolean;
  withUnknownSwitch?: boolean;
  unknownSwitchEnabled?: boolean;
  label?: string;
  field?: string;
  defaultValue?: WildcardTagsFilter;
  value?: WildcardTagsFilter;
  onChange: (value: WildcardTagsFilter) => void;
  labelFormatter?: TagsFilterLabelFormatters;
  active?: boolean;
}

const TagsFilterInput: React.FC<Props> = React.memo(
  ({
    label,
    withUnknownSwitch = false,
    unknownSwitchEnabled,
    field,
    defaultValue = withUnknownSwitch
      ? {
          includeEmpty: unknownSwitchEnabled === undefined ? true : unknownSwitchEnabled,
          value: [],
        }
      : {
          value: [],
        },
    onChange,
    root,
    labelFormatter,
    active,
  }) => {
    const [state, setState] = useState<WildcardTagsFilter>(defaultValue);
    const classes = useStyles();
    const { t } = useTranslation();
    const chipLabelRenderer = useCallback(
      (text: string) => {
        return labelFormatter && tagsLabelFormattersMap[labelFormatter]
          ? tagsLabelFormattersMap[labelFormatter](text)
          : text;
      },
      [labelFormatter],
    );

    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state, root);

    function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
      const pastedValues = e.clipboardData
        .getData('Text')
        .split(/\r\n|\r|\n|\t/)
        .filter((v) => v.trim());
      const allUniqueValues = new Set([...(state.value ?? []), ...pastedValues]);
      setState({ ...state, value: Array.from(allUniqueValues) });
    }

    const handleAddChip = (chip: string) => {
      setState({ ...state, value: [...(state.value ?? []), chip] });
    };

    const handleDeleteChip = (chip: string) => {
      const filtered = state.value?.filter((tag) => tag !== chip);
      setState({ ...state, value: filtered });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const splitString = inputValue.split(' ');
      const filteredSplitString = splitString.filter((el: string) => el !== '');

      if (filteredSplitString.length) {
        const componentState = [...(state.value ?? [])];
        let mergedArray = componentState.concat(filteredSplitString);
        mergedArray = mergedArray.filter((item, index) => {
          return mergedArray.indexOf(item) === index;
        });
        setState({
          ...state,
          value: mergedArray,
        });
      }
    };

    const rs: any[] = [];
    if (withUnknownSwitch && state.includeEmpty) {
      rs.push({ label: 'Included:', value: 'Companies with unknown data' });
    }
    if (state.value?.length) {
      rs.push({ label: 'Included:', value: state.value.join(', ') });
    }

    return (
      <>
        {active ? (
          <>
            {withUnknownSwitch && (
              <SwitchCompaniesUnknowns
                onChange={(value) => {
                  setState({ ...state, includeEmpty: value });
                }}
                checked={state.includeEmpty}
              />
            )}
            <div>
              {label ? (
                <InputLabel htmlFor={field} variant="outlined">
                  {label}
                </InputLabel>
              ) : null}
              <ChipInput
                variant="outlined"
                className={classes.chipInput}
                placeholder={t('app:useEnterForSearch')}
                value={state.value}
                onAdd={(chip: string) => handleAddChip(chip)}
                onDelete={(chip) => handleDeleteChip(chip)}
                onBlur={(e: any) => handleChange(e)}
                onPaste={(e: any) => handlePaste(e)}
                chipRenderer={(
                  { text, isFocused, isDisabled, isReadOnly, handleClick, handleDelete, className },
                  key,
                ) => (
                  <Chip
                    key={key}
                    className={className}
                    style={{
                      pointerEvents: isDisabled || isReadOnly ? 'none' : undefined,
                      backgroundColor: isFocused ? grey[400] : undefined,
                    }}
                    onClick={handleClick}
                    onDelete={handleDelete}
                    label={chipLabelRenderer(text)}
                  />
                )}
              />
            </div>
          </>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

const useStyles = makeStyles((theme) => ({
  chipInput: {
    width: '100%',
    '& input': {
      padding: theme.spacing(2),
      margin: theme.spacing(2),
    },
    '& div:first-child': {
      minHeight: theme.spacing(9),
    },
    '& .MuiInputBase-root': {
      height: 'auto !important',
      padding: '0',
    },
    '& .MuiButtonBase-root': {
      margin: toCssValue([theme.spacing(2), theme.spacing(1), theme.spacing(2), theme.spacing(2)]),
      height: 'auto',
    },
    '& .MuiChip-label': {
      whiteSpace: 'normal',
      padding: toCssValue([theme.spacing(2), theme.spacing(4)]),
      wordBreak: 'break-all',
    },
  },
}));

export default TagsFilterInput;
